/**
 * `useCycleRoster` — shared TanStack Query hook for `/api/cycles/{id}/roster`.
 *
 * Issue #127. Three Roster-tab consumers (`<RosterHeatmap/>`,
 * `<CoverageGapsSection/>`, the LeavesTab lead view) plus possibly more
 * fetched the same `/roster` payload via raw `useEffect` + `apiFetch`
 * keyed on `[cycle.id, cycle.state, accountId, editTick]`. Each
 * mount fired its own request → on tab swap, three (or more) parallel
 * GETs for the same payload, plus skeleton-flash on every re-mount
 * because the in-component `useState` cache was lost.
 *
 * This hook gives them all the same `["cycle-roster", cycleId, accountId]`
 * cache key so:
 *
 *   1. The first consumer to mount fills the cache; siblings hit it.
 *   2. Tab-swap re-mounts are no-op fetches within `staleTime`.
 *   3. The `editTick` cascade still works because consumers can call
 *      `query.refetch()` from a `useSolveTerminalRefetch` callback.
 *
 * `staleTime: 30_000` — chosen to align with the founder's mental
 * model "tab swaps shouldn't refetch within ~30s of activity." Longer
 * than `useCycle`'s 60s window would be fine semantically, but the
 * roster payload changes more often (every solve / every cell edit)
 * so a tighter window keeps the cache from going stale across solves
 * the user did NOT trigger from this tab.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";
import { liveRefetchOptions } from "./liveRefetch";
import type { components } from "./schema.gen";

export type CycleRosterResponse = components["schemas"]["RosterResponse"];
// Issue #710 — the member-facing endpoint returns a deliberately
// narrower payload (no `blocks`; assignments carry no note / takeover /
// swap_history / publish-state). `<RosterHeatmap/>` reads every one of
// those fields optionally, so the member shape is render-compatible —
// the missing inner-circle fields simply resolve to empty / undefined.
export type MemberCycleRosterResponse = components["schemas"]["MemberRosterResponse"];

const STALE_TIME_MS = 30_000;

/**
 * Build the canonical query key for the cycle-roster cache. Exported
 * so callers can `queryClient.invalidateQueries({ queryKey: ... })` on
 * mutations (e.g. publish, cell edit) without re-deriving the shape.
 *
 * Issue #710: the member-facing view fetches a different endpoint with
 * a stripped payload, so it is cached under a distinct key suffix to
 * avoid a member's read-only payload masquerading as the lead's full
 * payload in the shared cache (or vice-versa).
 */
export function cycleRosterQueryKey(
  cycleId: string,
  accountId: string | null,
  memberView = false,
  show: ShowMembers | null = null,
  versionId: string | null = null,
): readonly unknown[] {
  // #1909 — the Show filter is the LAST slot so the `[0]`/`[2]` predicate
  // in `cycleRosterQueryFilter` keeps matching every variant. #3002 — a
  // version read adds two slots after it (lead only), so every prefix key
  // without them still matches it.
  return [
    "cycle-roster",
    memberView ? "member" : "lead",
    cycleId,
    accountId ?? "",
    show ?? "",
    ...(versionId && !memberView ? ["version", versionId] : []),
  ];
}

/** #1909 / Build A1 — which member rows the roster read returns. */
export type ShowMembers = "deployed" | "everyone";

/**
 * Invalidation filter matching EVERY cached cycle-roster read for one
 * cycle — the lead and member variants, under whichever account they were
 * cached.
 *
 * Mutations that live outside the roster hooks (corrections apply,
 * mc-recover apply) have no `accountId` to hand `cycleRosterQueryKey`,
 * and the shorthand they used instead — `["cycle-roster", cycleId]` —
 * matches NOTHING, because the real key carries the view discriminator in
 * slot 1 and the cycle id in slot 2. A silent no-op: the grid kept showing
 * the pre-apply roster until something else refetched it. Invalidating by
 * predicate is the accountId-free equivalent.
 *
 * #1956 — the filter ALSO matches every cached team range-roster read
 * (`["team-range-roster", …]`, `useTeamRangeRoster`). The roster page reads
 * the grid by date range, and any range on screen may include this
 * window's dates; a mutation that changed this window's roster must
 * refetch it too. One filter, so every mutation that already invalidates
 * the per-window read (hand edits, the ladder, Report off sick, corrections,
 * solves, versions) refreshes the range read with no second call. Use
 * `invalidateRosterReads` (`api/rosterInvalidation.ts`) at call sites.
 *
 * #2173 — takes one window or several: an edit that touched several windows
 * invalidates them in ONE call, so the shared families (the range read, the
 * statistics, the day verdict) refetch once, not once per window.
 */
export function cycleRosterQueryFilter(cycleIds: string | Iterable<string>): {
  predicate: (query: { queryKey: readonly unknown[] }) => boolean;
} {
  const ids = new Set<unknown>(typeof cycleIds === "string" ? [cycleIds] : cycleIds);
  return {
    predicate: (query) =>
      (query.queryKey[0] === "cycle-roster" && ids.has(query.queryKey[2])) ||
      query.queryKey[0] === "team-range-roster" ||
      // #2111 — the roster statistics are computed from the live rows.
      query.queryKey[0] === "roster-statistics" ||
      // #2173 — and so is the lead's day verdict.
      query.queryKey[0] === "day-verdicts" ||
      // #2603 — the held re-solve's change list reads the same live rows.
      query.queryKey[0] === "held-resolve-changes" ||
      // #2797 — the Floor row draws the same live rows.
      query.queryKey[0] === "team-floor",
  };
}

/** The path a per-window roster read fetches. #3002 — `versionId` draws a
 *  saved version read-only (the lead read only; the member read never takes
 *  it). */
export function cycleRosterPath(
  cycleId: string,
  memberView = false,
  show: ShowMembers | null = null,
  versionId: string | null = null,
): string {
  const base = memberView
    ? `/api/cycles/${cycleId}/roster/member`
    : `/api/cycles/${cycleId}/roster`;
  const params = new URLSearchParams();
  if (show) params.set("show", show);
  if (versionId && !memberView) params.set("version", versionId);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * #1956 — the full query options for one window's roster read, so a
 * consumer reading several windows at once (`useQueries`, the roster
 * page's range grid) shares the exact cache entries `useCycleRoster` fills.
 */
export function cycleRosterQueryOptions(
  cycleId: string,
  accountId: string | null,
  memberView = false,
  show: ShowMembers | null = null,
  versionId: string | null = null,
) {
  return {
    queryKey: cycleRosterQueryKey(cycleId, accountId, memberView, show, versionId),
    staleTime: STALE_TIME_MS,
    retry: false as const,
    ...liveRefetchOptions,
    queryFn: () =>
      apiFetch<CycleRosterResponse>(cycleRosterPath(cycleId, memberView, show, versionId)),
  };
}

interface UseCycleRosterOpts {
  /**
   * Force-disable the fetch even when ids are present. Used by
   * `<RosterHeatmap/>` / `<CoverageGapsSection/>` to skip the request
   * on un-published cycles (the endpoint 404s pre-publish).
   */
  enabled?: boolean;
  /**
   * Issue #710 — fetch the MEMBER-safe endpoint
   * (`/api/cycles/{id}/roster/member`) instead of the lead endpoint.
   * Returns the same members x days grid read-only, but with all
   * inner-circle data stripped server-side. 404 when the team's
   * `member_full_roster` setting is off (the caller falls back to the
   * own-shifts-only personal roster).
   */
  memberView?: boolean;
  /**
   * #1909 / Build A1 — the toolbar's Show control: `deployed` (people with
   * a posting or an assignment in this schedule's dates) or `everyone`.
   * `null`/omitted lets the team's unposted rule decide; the response says
   * which it did in `members_shown`.
   */
  show?: ShowMembers | null;
  /**
   * #3002 — draw this saved version (a solve step, a solve result or a
   * saved roster copy) read-only, in its own cache entry. Lead read only.
   */
  versionId?: string | null;
}

export function useCycleRoster(cycleId: string, opts: UseCycleRosterOpts = {}) {
  // Auth is attached centrally by `apiFetch` via the module-level
  // TokenSource (#773), so this hook no longer threads an account id
  // for auth. It reads `useAuth().accountId` purely as the per-account
  // cache-key discriminator + the auth-ready enable gate (#853). The
  // value is identical to the id callers previously threaded down — in
  // both dev-shim and Clerk modes `useAuth().accountId` resolves to the
  // same account id those call-sites read from `useAuth()`.
  const { accountId } = useAuth();
  const enabledByCaller = opts.enabled ?? true;
  const memberView = opts.memberView ?? false;
  const show = opts.show ?? null;
  const versionId = opts.versionId ?? null;
  return useQuery<CycleRosterResponse, ApiError | Error>({
    // #90 — the published roster is a LIVE read: a member's phone report
    // off sick (or a withdrawal) has to reach an already-open lead window
    // without a manual reload. See `liveRefetch.ts` (spread inside the
    // options builder).
    ...cycleRosterQueryOptions(cycleId, accountId, memberView, show, versionId),
    enabled: Boolean(cycleId && accountId) && enabledByCaller,
  });
}
